import type {
  ClientPostChangeConfirmation,
  ClientPostCreateConfirmation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  type EdgeInsets,
  useSafeAreaInsets
} from 'react-native-safe-area-context';

import type { PostFeedTypeFeature } from '@/features/feature-management/types/postTypeFeature';
import { PostSubmitButton } from '@/features/post-editor/components/editor/PostSubmitButton';
import { PostForm } from '@/features/post-editor/components/PostForm';
import { usePostCreationForm } from '@/features/post-editor/hooks/usePostCreationForm';
import type {
  PostCreationChannelOption,
  PostCreationContext
} from '@/features/post-editor/types/postCreationOptions';
import type { PostFeedType } from '@/features/posts/types/postFeed';
import type { Sender } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { AppParam } from '@/navigation/types/navigation';
import { AppRoute } from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = {
  navigation: NativeStackNavigationProp<AppParam>;
  sender: Sender;
  creationContext: PostCreationContext;
  groupId?: string;
  channelType?: PostFeedType;
  geoAreaDepth?: number;
  autoSelectOrganizationGeoArea?: boolean;
  senderChannels?: PostCreationChannelOption[];
  senderChannelOptions?: PostFeedTypeFeature[];
};

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    body: {
      backgroundColor: theme.colors.surfaceContainerLow,
      paddingBottom: 32 + insets.bottom
    },
    submitButtonContainer: {
      paddingHorizontal: 16,
      marginTop: 24
    }
  });

export const PostCreationContent: FC<Props> = ({
  navigation,
  sender,
  creationContext,
  groupId,
  channelType,
  geoAreaDepth,
  autoSelectOrganizationGeoArea = false,
  senderChannels,
  senderChannelOptions
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });

  const handlePostSuccess = useCallback(
    (post: ClientPostCreateConfirmation | ClientPostChangeConfirmation) => {
      const postId = post.post.id;
      if (isEmptyOrNull(postId)) {
        navigation.pop();
        return;
      }
      navigation.replace(AppRoute.PostDetail, { id: postId });
    },
    [navigation]
  );

  const { form, media, modal, isCreating, isSubmitDisabled, submit } =
    usePostCreationForm({
      sender,
      creationContext,
      senderChannels,
      groupId,
      autoSelectOrganizationGeoArea,
      params: { channelType, groupId },
      onSuccess: handlePostSuccess
    });

  return (
    <View style={styles.container}>
      <StateView
        isLoading={isCreating}
        isError={false}
        stateLabels={t('state', { returnObjects: true })}
        contentContainerStyle={styles.body}
        backgroundColor={theme.colors.surfaceContainerLow}
        keyboardAware
        keyboardAwareMode="layout"
        keyboardBottomOffset={24}
        gestureScroll
        keyboardShouldPersistTaps="handled"
      >
        <PostForm
          form={form}
          sender={sender}
          media={media}
          groupId={groupId}
          modal={modal}
          geoAreaDepth={geoAreaDepth}
          senderChannels={senderChannels}
          senderChannelOptions={senderChannelOptions}
        />
        <View style={styles.submitButtonContainer}>
          <PostSubmitButton
            label={t('send')}
            disabled={isSubmitDisabled}
            onPress={submit}
          />
        </View>
      </StateView>
    </View>
  );
};
